import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '../../components/button';
import { Error } from '../../components/error';
import { Form } from '../../components/form';
import { HabitBundle } from '../../config/habit.config';
import { HabitTargetType } from '../../dto/habit.enum';
import { useFirebase } from '../../hooks/firebase.hook';
import { useHabit } from '../../hooks/habit.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';

interface FormData {
  target?: string;
  notes?: string;
}

export default function HabitBundleScreen(): JSX.Element {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { translateError } = useFirebase();
  const location = useLocation();
  const { startHabit } = useHabit();

  const originalBundle = location.state as HabitBundle;

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [habitIndexInEdit, setHabitIndexInEdit] = useState<number>();
  const [selectedHabits, setSelectedHabits] = useState<Set<string>>(() => new Set(originalBundle.habits.map((h) => h.title)));
  const [bundle, setBundle] = useState<HabitBundle>(() => structuredClone(originalBundle));

  const habitInEdit = habitIndexInEdit != null ? bundle.habits[habitIndexInEdit] : undefined;

  function resetBundle() {
    setBundle(structuredClone(originalBundle));
    setHabitIndexInEdit(undefined);
  }

  function toggleHabit(title: string) {
    setSelectedHabits((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  }

  function next() {
    const habitIndexToEdit = bundle.habits.findIndex((h) => h.notes == null && selectedHabits.has(h.title));
    if (habitIndexToEdit >= 0) {
      setHabitIndexInEdit(habitIndexToEdit);
    } else {
      done();
    }
  }

  function handleSubmit({ notes, target }: FormData) {
    if (habitInEdit) {
      habitInEdit.notes = notes ?? '';
      if (habitInEdit.targetConfig) habitInEdit.targetConfig.value = target != null ? +target : undefined;
    }

    next();
  }

  function done() {
    const habitsToSave = bundle.habits.filter((h) => selectedHabits.has(h.title));
    if (habitsToSave.length === 0) {
      navigate(-1);
      return;
    }

    setIsSaving(true);

    Promise.all(habitsToSave.map(startHabit))
      .then(() => navigate(-2))
      .catch((e) => setError(translateError(e.message)))
      .finally(() => setIsSaving(false));
  }

  useLayout(
    () => ({
      activeMenu: 'habits',
      title: habitInEdit ? t(habitInEdit.title) : t(bundle.name),
      titleSize: habitInEdit ? 'text-xl' : 'text-2xl',
      backButton: true,
      onBack: habitInEdit ? resetBundle : undefined,
    }),
    [t, bundle, habitIndexInEdit],
  );

  return habitInEdit ? (
    <Form key={habitIndexInEdit} onSubmit={handleSubmit}>
      {habitInEdit?.description && (
        <>
          <div className="text-sm text-base-content/70 whitespace-pre-line hyphens-auto">
            {t(habitInEdit.description)}
          </div>
          <div className="divider my-1" />
        </>
      )}

      <div className="flex flex-col gap-4">
        <div>
          <div className="text-base-content/70 mb-1">{t('What is your exact goal you want to achieve?')}</div>
          <textarea className="textarea textarea-lg w-full" placeholder={t('Personal goal')} name="notes" />
        </div>

        {habitInEdit.targetType === HabitTargetType.QUANTITY && (
          <div>
            <div className="text-base-content/70 mb-1">{t('How many or how much do you want to achieve?')}</div>
            <input
              type="number"
              name="target"
              className="input input-lg validator w-full"
              placeholder={t(habitInEdit.targetConfig?.valueHint ?? 'Target')}
              required
            />
            <div className="validator-hint hidden">{t('Required')}</div>
          </div>
        )}

        <Button className="btn btn-primary btn-lg" type="submit" loading={isSaving} onClick={next}>
          {t('Continue')}
        </Button>

        {error && <Error msg={error} />}
      </div>
    </Form>
  ) : (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-base-content/70">
        {t('The following habits are part of the package. Tap on a habit to exclude it. You can adjust the exact goals later.')}
      </div>

      <ul className="list bg-base-100 rounded-box shadow-md">
        {bundle.habits.map((h) => (
          <li
            key={h.title}
            className={`list-row gap-0 cursor-pointer transition-opacity ${!selectedHabits.has(h.title) && 'opacity-40'}`}
            onClick={() => toggleHabit(h.title)}
          >
            <div className={!selectedHabits.has(h.title) ? 'line-through' : ''}>
              <div className="font-bold text-lg">{t(h.title)}</div>
              {h.description && (
                <div className="font-normal text-base-content/70 whitespace-pre-line hyphens-auto">
                  {t(h.description)}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Button
        className="btn btn-primary btn-lg"
        type="submit"
        loading={isSaving}
        disabled={selectedHabits.size === 0}
        onClick={next}
      >
        {t('Start package')}
      </Button>

      {error && <Error msg={error} />}
    </div>
  );
}
